var AbstractReader = require("app/MediaPlayer/Tech/Abstract/AbstractReader"),
    CanvasView     = require("./CanvasView"),
    Control        = require("enyo/Control"),
    Page           = require("./Page"),
    Signals        = require("enyo/Signals"),
    Toolbar        = require("./Toolbar"),
    frameThrottle  = require("enyo-perf/utils").frameThrottle,
    get            = require("object-path").get,
    kind           = require("enyo/kind");

require("./Reader.less");

module.exports = kind(/** @lends  app.MediaPlayer.Tech.ImageReader.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.Tech.ImageReader",

    /**
     * @private
     */
    kind: AbstractReader,

    /**
     * @private
     */
    classes: "app-media-player-image-reader enyo-fit",

    /**
     * @private
     */
    supportsSafeArea: true,

    /**
     * @private
     */
    isReusable: true,

    /**
     * The state of this page.
     *
     * @type  {?Object}
     * @public
     */
    state: null,

    /**
     * @private
     */
    components: [{
        kind:    Signals,
        onkeyup: "handleKeyUp"
    }, {
        classes:    "app-media-player-image-reader__canvas-wrapper",
        components: [{
            name:     "canvasView",
            kind:     CanvasView,
            classes:  "app-media-player-image-reader__canvas-view",
            pageKind: Page
        }]
    }, {
        name:    "toolbar",
        kind:    Toolbar,
        classes: "app-media-player-image-reader__toolbar"
    }],

    /**
     * @private
     */
    bindings: [{
        from: ".activeIndex",
        to:   ".$.toolbar.activeIndex"
    }, {
        from: ".fullscreen",
        to:   ".$.toolbar.fullscreen"
    }, {
        from: ".$.canvasView",
        to:   ".$.toolbar.canvasView"
    }],

    /**
     * @private
     */
    observers: {
        update: ["state"]
    },

    /**
     * @private
     */
    handlers: {
        onRequestZoomOut:        "requestZoomOut",
        onRequestZoomIn:         "requestZoomIn",
        onRequestFirstPage:      "requestFirstPage",
        onRequestPreviousPage:   "requestPreviousPage",
        onRequestPage:           "requestPage",
        onRequestNextPage:       "requestNextPage",
        onRequestLastPage:       "requestLastPage",
        onRequestFullscreen:     "requestFullscreen",
        onRequestExitFullscreen: "cancelFullscreen"
    },

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.update = frameThrottle(this.bindSafely(this.update));
            sup.apply(this, arguments);
            this.observe("app.$.i18nService.translation", this.update);
            this.observe("$.canvasView.activePage", this.activePageChanged);
        };
    }),

    /**
     * @private
     */
    update: function() {
        var state     = this.state || {},
            mediaItem = get(state, "media", null),
            pages     = get(state, "watchingSession.urls.hq.path", []);

        // Insufficient data, revert to not ready state
        if (!mediaItem || pages.length < 1) {
            this.clearReader();
            this.resetPlayerState();

            return;
        }

        // Only activate when absoluteShowing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("update");

            return;
        }
        this.removeFromAbsoluteShowingQueue("update");

        // Ok, lets go
        this.resetPlayerState();
        this.set("isReady", 0);

        // Apply reader content
        this.hydrateReader();

        // Set isReady state
        this.set("isPlaying", true); // TODO: Whenever the page is loaded
        this.set("isReady", 1);
    },

    /**
     * @private
     */
    clearReader: function() {
        var $canvasView = this.$.canvasView;

        $canvasView.set("requestedIndex", null);
        $canvasView.set("data", null);
        this.$.toolbar.set("state", null);
    },

    /**
     * @private
     */
    hydrateReader: function() {
        var state         = this.state || {},
            sessionCursor = get(state, "watchingSession.cursor", 0),
            paramsCursor  = get(state, "params.cursor", null),
            pages         = get(state, "watchingSession.urls.hq.path", []),
            $canvasView   = this.$.canvasView,
            cursor;

        // Resolve cursor
        cursor = paramsCursor !== null
            ? parseInt(paramsCursor, 10) - 1
            : parseInt(sessionCursor, 10);
        cursor = Math.max(0, Math.min(pages.length - 1, cursor));

        // Apply pages to the canvas view
        $canvasView.set("requestedIndex", cursor);
        $canvasView.set("data", pages);
        this.$.toolbar.set("state", state);
        this.set("isSeekable", pages.length > 0);
    },

    /**
     * Verify that this player can be (re)used to play the supplied media.
     *
     * @param    {?Object}  mediaData  The media data
     * @returns  {boolean}  Whether this tech can play the supplied media
     * @public
     */
    isReusableFor: function(mediaData) {
        var mediaType = get(mediaData, "type", null);

        return this.isReusable
            && ["book", "issue"].includes(mediaType);
    },

    /**
     * Resets player to the state of an uninitialized player.
     *
     * @private
     */
    resetPlayerState: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.set("isSeekable", false);
            this.set("hasStarted", false);
            this.set("page", null);
        };
    }),

    /**
     * We use css flags as breakpoints because the reader might be minified, in
     * which case it's size is influenced by screen size.
     *
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var readerWidth;

            sup.apply(this, arguments);
            readerWidth = this.getBounds().width;

            if (readerWidth < 375) {
                this.setAttribute("data-break-point", "tiny");
            }
            else if (readerWidth >= 375 && readerWidth < 480) {
                this.setAttribute("data-break-point", "small");
            }
            else if (readerWidth >= 480 && readerWidth < 960) {
                this.setAttribute("data-break-point", "medium");
            }
            else if (readerWidth > 960) {
                this.setAttribute("data-break-point", "large");
            }
        };
    }),

    // Reader Key Commands
    // --------------------------------

    /**
     * Use keyboard keys to control the reader.
     *
     * @param  {enyo.Control}  sender  The sender
     * @param  {Object}        event   The event
     * @private
     */
    handleKeyUp: function(sender, event) {
        // Do nothing if the event was already processed
        if (event.defaultPrevented) {
            return;
        }

        // First page
        if (event.key === "Home") {
            this.requestFirstPage(sender, event);
        }

        // Previous page
        if (event.key === "Left" || event.key === "ArrowLeft") {
            this.requestPreviousPage(sender, event);
        }

        // Next page
        if (event.key === "Right" || event.key === "ArrowRight") {
            this.requestNextPage(sender, event);
        }

        // Last page
        if (event.key === "End") {
            this.requestLastPage(sender, event);
        }

        // Zoom in
        if (event.key === "Add" || event.key === "+") {
            this.requestZoomIn(sender, event);
        }

        // Zoom out
        if (event.key === "Subtract" || event.key === "-") {
            this.requestZoomOut(sender, event);
        }
    },

    // Reader UI Commands
    // --------------------------------

    /**
     * Forward the zoom event to the canvas.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop propagation
     * @private
     */
    requestZoomOut: function(sender, event) {
        if (this.isReady === 1) {
            this.$.canvasView.waterfallDown("onRequestZoomOut", event);
        }

        return true;
    },

    /**
     * Forward the zoom event to the canvas.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop propagation
     * @private
     */
    requestZoomIn: function(sender, event) {
        if (this.isReady === 1) {
            this.$.canvasView.waterfallDown("onRequestZoomIn", event);
        }

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestFirstPage: function() {
        if (this.isReady === 1) {
            this.$.canvasView.transitionToFirst();
        }

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestPreviousPage: function() {
        if (this.isReady === 1) {
            this.$.canvasView.transitionToPrevious();
        }

        return true;
    },

    /**
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop propagation
     * @private
     */
    requestPage: function(sender, event) {
        if (this.isReady === 1) {
            this.$.canvasView.transitionToIndex(event.page);
        }

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestNextPage: function() {
        if (this.isReady === 1) {
            this.$.canvasView.transitionToNext();
        }

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestLastPage: function() {
        if (this.isReady === 1) {
            this.$.canvasView.transitionToLast();
        }

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestFullscreen: kind.inherit(function(sup) {
        return function() {
            if (this.isReady === 1) {
                sup.apply(this, arguments);

                // Fallback full-screen mode doesn't produce any events, so we
                // produce one on our own.
                if (!Control.Fullscreen.nativeSupport()) {
                    Signals.send("onFullscreenChange");
                }
            }

            return true;
        };
    }),

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    cancelFullscreen: kind.inherit(function(sup) {
        return function() {
            if (this.isReady === 1) {
                sup.apply(this, arguments);

                // Fallback full-screen mode doesn't produce any events, so we
                // produce one on our own.
                if (!Control.Fullscreen.nativeSupport()) {
                    Signals.send("onFullscreenChange");
                }
            }

            return true;
        };
    }),

    /**
     * @private
     */
    activePageChanged: function() {
        var $canvasView = this.$.canvasView,
            activePage  = $canvasView.get("activePage") || null,
            activeIndex = $canvasView.getActiveIndex();

        if (!this.hasStarted && activePage && (activeIndex || activeIndex === 0)) {
            this.set("hasStarted", true);
        }

        this.set("activeIndex", activeIndex, true);
    },

    // Reader API
    // --------------------------------

    /**
     * Hide the controls.
     *
     * @returns  {boolean}  `true` if hidden, `false` otherwise.
     * @public
     */
    hideControls: function() {
        this.$.toolbar.hide();

        return true;
    },

    /**
     * Show the controls.
     *
     * @returns  {boolean}  `true` if showing, `false` otherwise.
     * @public
     */
    showControls: function() {
        this.$.toolbar.show();

        return true;
    },

    /**
     * Move to a certain page index
     *
     * @param  {number}  page  The page index to seek to
     *
     * @public
     */
    seek: function(page) {
        if (this.get("isSeekable")) {
            this.$.canvasView.transitionToIndex(page);
        }
        else {
            this.$.canvasView.set("requestedIndex", page);
        }
    },

    /**
     * Return the index of the current page displayed by the reader.
     *
     * @returns  {?number}  The current page index if available, `null`
     *                      otherwise.
     * @public
     */
    getCursor: function() {
        if (this.isReady === 1) {
            return this.get("activeIndex") || null;
        }
        else {
            return null;
        }
    },

    /**
     * Return the length in pages of the current content.
     *
     * @returns  {?number}  The current amount of pages.
     * @public
     */
    getLength: function() {
        if (this.isReady === 1) {
            return get(this, "state.watchingSession.length", null);
        }
        else {
            return null;
        }
    },

    /**
     * Destroys the current VimeoPlayer instance and resets state.
     *
     * @private
     */
    destroyReader: function() {
        this.resetReaderState();
    },

    /**
     * Destroy the reader component.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.destroyReader();
            sup.apply(this, arguments);
        };
    })

});
